/**
 * 9jaWin Admin API Module
 *
 * Admin access is server-authoritative. Authentication is carried by the
 * hardened HttpOnly access/refresh cookie session managed by services/api.js.
 * No access or refresh token is read from or written to localStorage here.
 */

import api, { clearAuthStorage } from './api';


let adminApiInstance = null;
let verified = false;
let verifiedAt = 0;
const ADMIN_VERIFY_TTL_MS = 30 * 1000;
const ALLOWED_SETTINGS_KEYS = new Set(['game_whot_enabled','matchmaking_enabled','bot_system_enabled','bot_difficulty','stake_wait_timeout_seconds','turn_timeout_seconds','match_timeout_seconds','whot_reconnect_grace_seconds','whot_stake_bot_enabled','whot_stake_bot_fallback_delay_seconds','whot_stake_bot_easy_enabled','whot_stake_bot_medium_enabled','whot_stake_bot_hard_enabled','whot_stake_bot_max_fill','whot_stake_bot_min_real_players','whot_stake_bot_tiers','min_deposit','max_deposit','min_withdrawal','max_withdrawal','min_stake','max_stake','withdrawal_fee_percent','platform_fee_2player','platform_fee_4player','platform_cut_2player_percent','platform_cut_4player_percent','maintenance_mode','registration_enabled','allow_self_service_email_change','max_friends','referral_bonus_percent','referral_commission_percent','promo_lifetime_commission_percent','promo_unlock_qualified_referrals','financial_deposits_enabled','financial_withdrawals_enabled','referral_system_enabled','prime_system_enabled', ...Array.from({ length: 11 }, (_, index) => `prime_threshold_${index}`), 'whot_seasons_enabled','maximum_rank_achievement_seasons','loader_enabled','loader_startup_hide_delay_ms','loader_startup_remove_delay_ms','ai_auto_escalation','duplicate_signup_prevention_enabled','duplicate_signup_excluded_emails','duplicate_signup_unknown_device_ip_action','withdrawal_name_match_enabled','withdrawal_name_min_matching_words','withdrawal_name_partial_action','withdrawal_name_no_match_action','withdrawal_one_bank_account_per_user_enabled']);

export async function getAdminApi() {
  if (adminApiInstance && verified && (Date.now() - verifiedAt) < ADMIN_VERIFY_TTL_MS) return adminApiInstance;

  try {
    const res = await api.get('/admin/check', { skipAuthRedirect: true });
    if (!res.ok && res.status >= 400) {
      adminApiInstance = null;
      verified = false;
      return null;
    }
    const profile = res.data && typeof res.data === 'object' ? res.data : res;
    verified = true;
    verifiedAt = Date.now();
    adminApiInstance = {
      profile,
      permissions: Array.isArray(profile.permissions) ? profile.permissions : [],
      roleName: profile.roleName || null,
      roleSlug: profile.roleSlug || null,
      check: () => api.get('/admin/check', { skipAuthRedirect: true }),
      staff: {
        list: () => api.get('/admin/staff'),
        get: (id) => api.get('/admin/staff/' + encodeURIComponent(id)),
        invite: (data) => api.post('/admin/staff/invitations', data),
        pendingInvitations: () => api.get('/admin/staff/invitations/pending'),
        cancelInvitation: (id) => api.post('/admin/staff/invitations/' + encodeURIComponent(id) + '/cancel'),
        lookup: (email) => api.get('/admin/staff/lookup', { params: { email } }),
        grant: (data) => api.post('/admin/staff/grant', data),
        changeRole: (id, roleSlug) => api.patch('/admin/staff/' + encodeURIComponent(id) + '/role', { roleSlug }),
        suspend: (id) => api.post('/admin/staff/' + encodeURIComponent(id) + '/suspend'),
        revoke: (id) => api.post('/admin/staff/' + encodeURIComponent(id) + '/revoke'),
      },
      security: {
        sessions: () => api.get('/admin/security/sessions'),
        revokeSession: (id, reason) => api.post(`/admin/security/sessions/${id}/revoke`, { reason }),
        protectedAccounts: () => api.get('/admin/security/protected-accounts'),
      },
      audit: {
        view: (view, params = {}) => api.get('/admin/audit', { params: { ...params, view } }),
      },
      identity: {
        users: (params = {}) => api.get('/admin/identity/users', { params }),
        blockedSignups: (params = {}) => api.get('/admin/identity/blocked-signups', { params }),
        whitelist: () => api.get('/admin/identity/whitelist'),
        addWhitelist: (data) => api.post('/admin/identity/whitelist', data),
        removeWhitelist: (id) => api.delete('/admin/identity/whitelist/' + encodeURIComponent(id)),
        clearSignupIdentity: (id, reason) => api.post('/admin/identity/users/' + encodeURIComponent(id) + '/clear-signup-identity', { reason }),
        changeRegisteredName: (id, data) => api.put('/admin/identity/users/' + encodeURIComponent(id) + '/registered-name', data),
        nameReviewQueue: (params = {}) => api.get('/admin/identity/name-review-queue', { params }),
        approveNameReview: (id, reason) => api.post('/admin/identity/name-review-queue/' + encodeURIComponent(id) + '/approve', { reason }),
        rejectNameReview: (id, reason) => api.post('/admin/identity/name-review-queue/' + encodeURIComponent(id) + '/reject', { reason }),
        unlinkAccount: (id, reason) => api.delete('/admin/identity/accounts/' + encodeURIComponent(id) + '/unlink', { data: { reason } }),
      },
      systemHealth: () => api.get('/admin/system-health'),
      maintenance: {
        status: () => api.get('/admin/maintenance/status'),
        toggle: (data) => api.post('/admin/maintenance/toggle', data),
        addExcluded: (email) => api.post('/admin/maintenance/excluded', { email }),
        removeExcluded: (email) => api.delete('/admin/maintenance/excluded', { data: { email } }),
      },
      deployV2: { status: () => api.get('/admin/deploy-v2/status'), tags: () => api.get('/admin/deploy-v2/tags'), preview: (params) => api.get('/admin/deploy-v2/preview', { params }), action: (data) => api.post('/admin/deploy-v2/action', data) },
      appVersion: {
        status: () => api.get('/admin/app-version/status'),
        publish: (data) => api.post('/admin/app-version/publish', data),
        revert: (confirmation, pin) => api.post('/admin/app-version/revert', { confirmation, pin }),
      },
      promoCodes: {
        list: (params = {}) => api.get('/admin/promo-codes/list', { params }),
        searchUsers: (search) => api.get('/admin/promo-codes/users', { params: { search } }),
        getUser: (id) => api.get('/admin/promo-codes/users/' + id),
        assign: (id) => api.post('/admin/promo-codes/users/' + id + '/assign'),
        setLimit: (id, usageLimit) => api.patch('/admin/promo-codes/' + id + '/limit', { usageLimit }),
        enable: (id) => api.post('/admin/promo-codes/' + id + '/enable'),
        disable: (id) => api.post('/admin/promo-codes/' + id + '/disable'),
      },
      users: {
        list: (params) => api.get('/admin/users', { params }),
        get: (id) => api.get(`/admin/users/${id}`),
        authentication: (id) => api.get(`/admin/users/${id}/authentication`),
        whotHistory: (id, params) => api.get(`/admin/users/${id}/whot-history`, { params }),
        tournamentHistory: (id, params) => api.get(`/admin/users/${id}/tournament-history`, { params }),
        rooms: (id, params) => api.get(`/admin/users/${id}/rooms`, { params }),
        achievements: (id) => api.get(`/admin/users/${id}/achievements`),
        unifiedDiagnostic: (id) => api.get('/admin/match-investigation', { params: { userId: id }, skipAuthRedirect: true }),
        transactions: (id, params) => api.get('/admin/transactions', { params: { ...(params || {}), userId: id } }),
        updateStatus: (id, data) => api.post(`/admin/users/${id}/status`, data),
        updateEmail: (id, data) => api.post(`/admin/users/${id}/email`, data),
        walletAdjustment: (id, data) => api.post(`/admin/users/${id}/wallet-adjustment`, data),
        walletUnlock: (id, data) => api.post(`/admin/users/${id}/wallet-unlock`, data),
      },
      finance: { overview: () => api.get('/admin/finance/overview'), wallets: (params) => api.get('/admin/finance/wallets', { params }) },
      stats: {
        summary: () => api.get('/admin/stats'),
        analytics: {
          summary: () => api.get('/admin/analytics/summary'),
          revenueByGame: () => api.get('/admin/analytics/revenue-by-game'),
          signupsTrend: () => api.get('/admin/analytics/signups-trend'),
        },
      },
      transactions: {
        list: (params) => api.get('/admin/transactions', { params }),
        get: (id) => api.get(`/admin/transactions/${id}`),
        pendingWithdrawals: () => api.get('/admin/transactions/pending-withdrawals'),
      },
      leaderboardRewards: {
        overview: () => api.get('/admin/leaderboard-rewards/overview'),
        preview: (params) => api.get('/admin/leaderboard-rewards/preview', { params }),
        reviewQueue: () => api.get('/admin/leaderboard-rewards/review-queue'),
        updateSettings: (settings) => api.put('/admin/leaderboard-rewards/settings', { settings }),
        approve: (id, reason) => api.post('/admin/leaderboard-rewards/rewards/' + encodeURIComponent(id) + '/approve', { reason }),
        reject: (id, reason) => api.post('/admin/leaderboard-rewards/rewards/' + encodeURIComponent(id) + '/reject', { reason }),
      },
      settings: {
        getAll: () => api.get('/admin/settings'),
        update: (key, value) => {
          if (typeof key !== 'string' || !ALLOWED_SETTINGS_KEYS.has(key)) {
            return Promise.reject(new Error('This setting key is not allowlisted for updates.'));
          }
          return api.put('/admin/settings/' + encodeURIComponent(key), { value });
        },
      },
      stakeSettings: {
        getAll: () => api.get('/admin/stake-tiers'),
        updatePayoutRules: (data) => api.put('/admin/stake-tiers/payout-rules', data),
        createTier: (data) => api.post('/admin/stake-tiers', data),
        disableTier: (id) => api.post('/admin/stake-tiers/' + id + '/disable'),
        enableTier: (id) => api.post('/admin/stake-tiers/' + id + '/enable'),
        editTier: (id, data) => api.patch('/admin/stake-tiers/' + id, data),
        deleteTier: (id) => api.delete('/admin/stake-tiers/' + id),
      },
      emailSettings: {
        getAll: () => api.get('/admin/email-settings'),
        update: (channel, data) => api.put(`/admin/email-settings/${channel}`, data),
        test: (channel, recipient) => api.post(`/admin/email-settings/${channel}/test`, { recipient }),
      },
      paymentCredentials: {
        getFlutterwave: () => api.get('/admin/payment-credentials/flutterwave'),
        updateFlutterwave: (data) => api.put('/admin/payment-credentials/flutterwave', data),
      },
      payments: {
        overview: () => api.get('/admin/payments/overview'),
        providers: () => api.get('/admin/payments/providers'),
      },
      friendsRooms: {
        list: (params) => api.get('/admin/friends-rooms', { params }),
        get: (id) => api.get(`/admin/friends-rooms/${id}`),
      },
      notifications: { view: (view, params = {}) => api.get('/admin/notifications', { params: { ...params, view } }) },
      tournaments: {
        getAll: (params) => api.get('/tournaments/admin/all', { params }),
        get: (id) => api.get(`/tournaments/admin/${id}`),
        create: (data) => api.post('/tournaments', data),
        update: (id, data) => api.put(`/tournaments/${id}`, data),
        cancel: (id, data = {}) => api.post(`/tournaments/${id}/cancel`, data),
        delete: (id) => api.delete(`/tournaments/${id}`),
        pendingPayout: () => api.get('/admin/tournaments/pending-payout'),
        processPayout: (id, reason) => api.post(`/admin/tournaments/${id}/process-payout`, { ...(reason ? { reason } : {}) }),
        approvePayouts: (id, reason) => api.post(`/admin/tournaments/${id}/approve-payouts`, { ...(reason ? { reason } : {}) }),
        adjustPayout: (payoutId, amount, idempotencyKey) => api.put(`/admin/tournaments/payouts/${payoutId}`, { amount, idempotencyKey }),
        getAllPayouts: () => api.get('/admin/tournaments/payouts'),
      },
      support: {
        list: (params) => api.get('/admin/support/tickets', { params }),
        lookup: (reference) => api.get(`/admin/support/lookup/${reference}`),
        update: (id, data) => api.put(`/admin/support/tickets/${id}`, data),
      },
      announcements: {
        list: (params) => api.get('/announcements/admin/list', { params }),
        audienceCount: (audienceType, audienceConfig) => api.get('/announcements/admin/audience-count', { params: { audienceType, audienceConfig: JSON.stringify(audienceConfig || {}) } }),
        searchUsers: (q) => api.get('/announcements/admin/users/search', { params: { q } }),
        templates: () => api.get('/announcements/admin/templates'),
        create: (data) => api.post('/announcements/admin', data),
        createFromTemplate: (key, data) => api.post(`/announcements/admin/templates/${key}`, data),
        duplicate: (id) => api.post(`/announcements/admin/${id}/duplicate`),
        analytics: (id) => api.get(`/announcements/admin/${id}/analytics`),
        history: (id) => api.get(`/announcements/admin/${id}/history`),
        preview: (id) => api.get(`/announcements/admin/${id}/preview`),
        get: (id) => api.get(`/announcements/admin/${id}`),
        update: (id, data) => api.put(`/announcements/admin/${id}`, data),
        publish: (id, data) => api.post(`/announcements/admin/${id}/publish`, data),
        schedule: (id) => api.post(`/announcements/admin/${id}/schedule`),
        cancelSchedule: (id) => api.post(`/announcements/admin/${id}/cancel-schedule`),
        archive: (id) => api.post(`/announcements/admin/${id}/archive`),
        media: () => api.get('/announcements/admin/media'),
        registerMedia: (data) => api.post('/announcements/admin/media/register', data),
        aiDraft: (data) => api.post('/announcements/admin/ai/draft', data),
        campaigns: () => api.get('/announcements/admin/campaigns'),
      },
      achievements: {
        view: (view='definitions') => api.get('/admin/achievements', { params: { view } }),
        list: () => api.get('/admin/achievements'),
        create: (data) => api.post('/admin/achievements', data),
        update: (id, data) => api.patch(`/admin/achievements/${id}`, data),
        grant: (id, data) => api.post(`/admin/achievements/${id}/grant`, data),
        stats: (id) => api.get(`/admin/achievements/${id}/stats`),
      },
      prime: {
        status: (userId) => api.get('/admin/prime/status', { params: userId ? { userId } : {} }),
        searchUsers: (search) => api.get('/admin/prime/users', { params: { search } }),
        setOverride: (userId, level) => api.post(`/admin/prime/users/${userId}/override`, { level }),
        returnToAutomatic: (userId) => api.post(`/admin/prime/users/${userId}/automatic`),
      },
      whotOperations: {
        overview: () => api.get('/admin/whot/overview'),
        view: (view, gameId, params = {}) => api.get('/admin/whot/operations', { params: { view, ...(gameId ? { gameId } : {}), ...params } }),
      },
      whotSeason: {
        get: () => api.get('/admin/whot-season'),
        history: () => api.get('/admin/whot-season/history'),
        end: () => api.post('/admin/whot-season/end', { confirm: true }),
        start: () => api.post('/admin/whot-season/start', { confirm: true }),
        extend: (newEndAt) => api.post('/admin/whot-season/extend', { confirm: true, newEndAt }),
      },
      softwareAudit: {
        health: () => api.get('/admin/software-audit/health'),
        toolchain: () => api.get('/admin/software-audit/toolchain'),
        scans: (params = {}) => api.get('/admin/software-audit/scans', { params }),
        scan: (id) => api.get(`/admin/software-audit/scans/${encodeURIComponent(id)}`),
        findings: (id, params = {}) => api.get(`/admin/software-audit/scans/${encodeURIComponent(id)}/findings`, { params }),
        create: (data) => api.post('/admin/software-audit/scans', data),
        cancel: (id) => api.post(`/admin/software-audit/scans/${encodeURIComponent(id)}/cancel`),
        retry: (id) => api.post(`/admin/software-audit/scans/${encodeURIComponent(id)}/retry`),
        reports: (id) => api.get(`/admin/software-audit/scans/${encodeURIComponent(id)}/reports`),
        downloadReport: async (id, name) => {
          const r = await api.get(`/admin/software-audit/scans/${encodeURIComponent(id)}/reports/${encodeURIComponent(name)}`, { responseType: 'blob' });
          const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        downloadEvidence: async (scanId, artifactId) => {
          const r = await api.get(`/admin/software-audit/scans/${encodeURIComponent(scanId)}/evidence/${encodeURIComponent(artifactId)}`, { responseType: 'blob' });
          const disposition = r.headers?.['content-disposition'] || ''; const match = disposition.match(/filename="([^"]+)"/); const name = match?.[1] || artifactId;
          const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
      },
        platformAudit: {
          categories: () => api.get('/admin/software-audit/platform/categories'),
          list: () => api.get('/admin/software-audit/platform'),
          run: (category = null) => api.post('/admin/software-audit/platform/run', category ? { category } : {}),
          get: (id) => api.get(`/admin/software-audit/platform/${encodeURIComponent(id)}`),
          findings: (id) => api.get(`/admin/software-audit/platform/${encodeURIComponent(id)}/findings`),
          evidence: (id) => api.get(`/admin/software-audit/platform/${encodeURIComponent(id)}/evidence`),
          category: (id, category) => api.get(`/admin/software-audit/platform/${encodeURIComponent(id)}/category/${encodeURIComponent(category)}`),
          report: (id) => api.get(`/admin/software-audit/platform/${encodeURIComponent(id)}/report`),
          overview: (id) => api.get(`/admin/software-audit/platform/${encodeURIComponent(id)}/overview`),
          inventory: (id) => api.get(`/admin/software-audit/platform/${encodeURIComponent(id)}/inventory`),
          changes: (id) => api.get(`/admin/software-audit/platform/${encodeURIComponent(id)}/changes`),
          coverage: (id) => api.get(`/admin/software-audit/platform/${encodeURIComponent(id)}/coverage`),
          telemetry: () => api.get('/admin/software-audit/platform/telemetry'),
          errors: () => api.get('/admin/software-audit/platform/errors'),
          incidents: () => api.get('/admin/software-audit/platform/incidents'),
          openIncidents: () => api.get('/admin/software-audit/platform/incidents/open'),
          realtime: () => api.get('/admin/software-audit/platform/realtime'),
          runtimeInventory: () => api.get('/admin/software-audit/platform/runtime-inventory'),
          runtimeSessions: () => api.get('/admin/software-audit/platform/runtime-sessions'),
          runtimeSession: (id) => api.get(`/admin/software-audit/platform/runtime-sessions/${encodeURIComponent(id)}`),
        },
      axios: api,
    };
    return adminApiInstance;
  } catch (error) {
    adminApiInstance = null;
    verified = false;
    verifiedAt = 0;
    if (error?.response?.status === 401) {
      clearAuthStorage();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') window.location.assign('/login');
    }
    return null;
  }
}

export function clearAdminApi() {
  adminApiInstance = null;
  verified = false;
  verifiedAt = 0;
}
